import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Link, useNavigate, useParams } from 'react-router-dom'
import { MessagesSquare, ChevronsLeftRight } from 'lucide-react'
import { useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import { EmptyState } from '@/components/shared/feedback'
import { cn } from '@/lib/utils'
import {
  useConversation,
  useConversationCounts,
  useInfiniteConversations,
  useConversationListRealtime,
  useMarkRead,
  useMarkAllRead,
  type ConvTab,
  type InboxView,
} from '@/hooks/use-conversations'
import { useWidgets, useInboxAccounts } from '@/hooks/use-integrations'
import { groupOfPlatform, groupsPresent } from '@/lib/channel-groups'
import type { ChannelFilterValue } from '@/components/shared/channel-chips'
import { ConversationList } from './conversation-list'
import { groupsWithManyAccounts } from './channel-label'
import { ChatPanel } from './chat-panel'
import { ContactPanel } from './contact-panel'
import { FilterRail, FILTER_LABEL, type ConvFilter } from './filter-rail'
import { MobileInboxHeader, stripAccounts } from './mobile-inbox-header'
import { BottomSheet, BottomSheetContent } from '@/components/ui/bottom-sheet'
import { useIsMobile } from '@/hooks/use-is-mobile'

/** Bộ lọc gửi lên backend dạng `?view=` thay vì cờ riêng. */
const VIEW_FILTERS: InboxView[] = ['mine', 'unassigned', 'ai', 'direct', 'group']
import { AddFriendDialog } from './add-friend-dialog'
import { NewConversationDialog } from './new-conversation-dialog'
import { AppRail } from './app-rail'
import { ConnectChannelsOnboarding } from './connect-channels-onboarding'
import { useAiBots } from '@/hooks/use-ai'
import { AI_PATHS, isDefaultBot } from '@/lib/ai-khung'
import { useAuthStore } from '@/stores/auth-store'
import { loadInboxAccount, resolveSavedAccount, saveInboxAccount } from '@/lib/inbox-account'
import { Button } from '@/components/ui/button'
import { Sparkles } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { ConnectionsDialog, AppSettingsDialog, useMiniApps } from './embedded-apps-dialogs'
import { useInboxViewCounts } from '@/hooks/use-inbox-views'
import { useInboxFilter } from './use-inbox-filter'
import { FilterPopover } from './filter-popover'
import { SavedViewsSection, ViewDialog, type ViewDialogState } from './saved-views'

/** Trang Hội thoại — inbox realtime: cột lọc + danh sách + khung chat + hồ sơ KH. */
export function ConversationsPage() {
  const { t } = useTranslation('convList')
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const { id: activeId } = useParams<{ id: string }>()
  const isMobile = useIsMobile()
  // Điện thoại: hồ sơ khách mở bằng hộp trượt từ nút ⓘ ở đầu khung chat (cột phải không còn chỗ).
  const [infoOpen, setInfoOpen] = useState(false)
  // Điện thoại: vị trí cuộn danh sách — sống ở ĐÂY (ConversationsPage không unmount khi đổi route
  // /conversations ⇄ /conversations/:id) vì ConversationList con thì CÓ unmount mỗi khi mở 1 hội thoại
  // (xem nhánh activeId bên dưới) — xem hooks/use-scroll-memory.ts.
  const listScrollRef = useRef(0)

  const [filter, setFilter] = useState<ConvFilter>('all')
  // Tài khoản kênh đang chọn được NHỚ theo người dùng (chuyển mục rồi quay lại Nhắn tin không bị về "Tất cả" — lib/inbox-account.ts).
  const meId = useAuthStore((s) => s.user?.id)
  const [accountId, setAccountId] = useState<string | undefined>(() => loadInboxAccount(meId))
  const [tag, setTag] = useState<string | undefined>()
  const [searchInput, setSearchInput] = useState('')
  const [search, setSearch] = useState('')
  // Tab kiểu Zalo: "Ưu tiên" (khách) / "Nội bộ" (không tính báo cáo).
  const [tab, setTab] = useState<ConvTab>('main')
  // Lọc theo nhóm kênh (Zalo cá nhân, OA, Facebook…) — chip trên danh sách.
  const [channel, setChannel] = useState<ChannelFilterValue>('all')
  const { data: accounts } = useInboxAccounts()
  // Tài khoản đã nhớ mà không còn (xoá / mất quyền) → về "Tất cả"; mỗi lần đổi thì nhớ lại (chọn "Tất cả" = xoá bản nhớ).
  useEffect(() => {
    const ok = resolveSavedAccount(accountId, accounts)
    if (ok !== accountId) setAccountId(ok)
  }, [accounts, accountId])
  useEffect(() => { saveInboxAccount(meId, accountId) }, [meId, accountId])
  const channelGroups = useMemo(() => groupsPresent((accounts ?? []).map((a) => a.platform)), [accounts])
  // Nhiều tài khoản cùng loại (nhiều OA / Fanpage…) → dòng hội thoại hiện tên tài khoản kênh.
  const manyAccountGroups = useMemo(() => groupsWithManyAccounts(accounts ?? []), [accounts])
  // Nhóm đang chọn không còn tài khoản nào → về "Tất cả" (khỏi kẹt ở danh sách rỗng).
  const channelParam =
    channel !== 'all' && channelGroups.some((g) => g.id === channel) ? channel : undefined

  // Tài khoản và nhóm kênh là hai bộ lọc chồng nhau: chọn cái này mà lệch cái
  // kia thì danh sách trống không lý do. Đổi một bên thì gỡ bên còn lại nếu lệch.
  const groupOfAccount = (id?: string) =>
    groupOfPlatform((accounts ?? []).find((a) => a.id === id)?.platform)
  function handleAccountChange(id?: string) {
    setAccountId(id)
    if (id && channel !== 'all' && groupOfAccount(id) !== channel) setChannel('all')
  }
  function handleChannelChange(v: ChannelFilterValue) {
    setChannel(v)
    if (accountId && v !== 'all' && groupOfAccount(accountId) !== v) setAccountId(undefined)
  }
  // Chế độ xem tuỳ chỉnh (?sv=) + bộ lọc nâng cao tạm thời (nút phễu) — use-inbox-filter.ts
  const inbox = useInboxFilter()
  const [viewDialog, setViewDialog] = useState<ViewDialogState>({ open: false })
  const { data: viewCounts } = useInboxViewCounts({ tab, accountId, channel: channelParam })
  // Chọn bộ lọc có sẵn = rời chế độ xem tuỳ chỉnh (và bỏ điều kiện sửa tạm).
  function handleFilterChange(f: ConvFilter) {
    setFilter(f)
    setAiBot('')
    inbox.selectView(undefined)
  }
  // Bộ lọc "Nhân viên AI" ▸ một nhân viên AI ('' = tất cả, 'default' = AI chung của tổ chức — chỉ server cũ).
  const [aiBot, setAiBot] = useState('')
  // Sidebar nhận realtime từ phòng org kể cả khi chưa mở hội thoại nào
  useConversationListRealtime()

  const [addFriendOpen, setAddFriendOpen] = useState(false)
  const [newConvOpen, setNewConvOpen] = useState(false)
  const [connectionsOpen, setConnectionsOpen] = useState(false)
  const [appSettingsOpen, setAppSettingsOpen] = useState(false)
  const { activeApps } = useMiniApps()

  // Chiều rộng cột thông tin khách hàng — kéo được, tối đa nửa màn hình, nhớ giữa các lần dùng
  const RIGHT_MIN = 260
  /** Cột danh sách hội thoại: mặc định 320px, thu gọn tối đa còn 3/4 (240px). */
  const LIST_MAX = 320
  const LIST_MIN = 240
  const [rightWidth, setRightWidth] = useState<number>(() => {
    const saved = Number(localStorage.getItem('ecdp_contact_panel_w'))
    return Number.isFinite(saved) && saved >= RIGHT_MIN ? saved : 288
  })
  const [resizing, setResizing] = useState(false)

  const startResize = useCallback(
    (e: React.MouseEvent) => {
      e.preventDefault()
      const startX = e.clientX
      const startW = rightWidth
      const maxW = Math.floor(window.innerWidth / 2) // tối đa nửa màn hình
      setResizing(true)

      const onMove = (ev: MouseEvent) => {
        // Kéo sang trái = rộng ra (cột nằm bên phải)
        const next = Math.min(maxW, Math.max(RIGHT_MIN, startW - (ev.clientX - startX)))
        setRightWidth(next)
      }
      const onUp = () => {
        setResizing(false)
        document.removeEventListener('mousemove', onMove)
        document.removeEventListener('mouseup', onUp)
        document.body.style.userSelect = ''
        document.body.style.cursor = ''
      }
      document.body.style.userSelect = 'none'
      document.body.style.cursor = 'col-resize'
      document.addEventListener('mousemove', onMove)
      document.addEventListener('mouseup', onUp)
    },
    [rightWidth],
  )

  // Cột danh sách hội thoại: kéo mép phải để thu gọn, tối đa còn 3/4 bề rộng mặc định.
  const [listWidth, setListWidth] = useState<number>(() => {
    const v = Number(localStorage.getItem('ecdp_conv_list_w'))
    return v >= LIST_MIN && v <= LIST_MAX ? v : LIST_MAX
  })
  const [listResizing, setListResizing] = useState(false)
  const startListResize = useCallback(
    (e: React.MouseEvent) => {
      e.preventDefault()
      const startX = e.clientX
      const startW = listWidth
      setListResizing(true)
      const onMove = (ev: MouseEvent) => {
        setListWidth(Math.min(LIST_MAX, Math.max(LIST_MIN, startW + (ev.clientX - startX))))
      }
      const onUp = () => {
        setListResizing(false)
        document.removeEventListener('mousemove', onMove)
        document.removeEventListener('mouseup', onUp)
        document.body.style.userSelect = ''
        document.body.style.cursor = ''
      }
      document.body.style.userSelect = 'none'
      document.body.style.cursor = 'col-resize'
      document.addEventListener('mousemove', onMove)
      document.addEventListener('mouseup', onUp)
    },
    [listWidth],
  )
  useEffect(() => {
    if (!listResizing) {
      try { localStorage.setItem('ecdp_conv_list_w', String(listWidth)) } catch { /* bỏ qua */ }
    }
  }, [listResizing, listWidth])

  // Lưu lại chiều rộng khi thả chuột
  useEffect(() => {
    if (!resizing) localStorage.setItem('ecdp_contact_panel_w', String(rightWidth))
  }, [resizing, rightWidth])

  // Debounce ô tìm kiếm.
  useEffect(() => {
    const t = setTimeout(() => setSearch(searchInput.trim()), 300)
    return () => clearTimeout(t)
  }, [searchInput])

  const { data, isLoading, isError, refetch, hasNextPage, fetchNextPage, isFetchingNextPage } = useInfiniteConversations({
    tab,
    channel: channelParam,
    search,
    accountId,
    tag,
    unread: filter === 'unread',
    unreplied: filter === 'unreplied',
    pinned: filter === 'pinned',
    archived: filter === 'archived',
    view: VIEW_FILTERS.includes(filter as InboxView) ? (filter as InboxView) : undefined,
    aiBot: filter === 'ai' && aiBot ? aiBot : undefined,
    ...inbox.query,
  })
  // Tham chiếu ổn định: danh sách dựng lại bộ quan sát cuộn mỗi khi hàm này đổi.
  const loadMoreConversations = useCallback(() => { void fetchNextPage() }, [fetchNextPage])
  const isViewFilter = VIEW_FILTERS.includes(filter as InboxView) && !inbox.activeView && !tag
  // Ở chế độ lưu trữ, badge đếm phải theo tập hội thoại đã lưu trữ, không lẫn với đang chạy.
  // Đếm riêng từng tab (cùng bộ lọc tài khoản/kênh): badge cột lọc theo tab
  // đang mở, nhãn tab hiện số chưa đọc của chính tab đó như Zalo.
  const countScope = { accountId, channel: channelParam }
  const { data: mainCounts } = useConversationCounts('main', filter === 'archived', countScope)
  const { data: internalCounts } = useConversationCounts('internal', filter === 'archived', countScope)
  const counts = tab === 'internal' ? internalCounts : mainCounts
  // Lần đầu vào: chưa kênh nào (kể cả Live Chat) và chưa hội thoại nào → màn kết
  // nối kênh thay cho "Chọn một hội thoại". Chờ đủ dữ liệu để không nháy màn.
  const { data: widgets } = useWidgets()
  // AI Agent là MODULE (nhân viên AI), bộ lọc chỉ là lối vào: chưa có nhân viên
  // AI nào thì mời tạo ngay tại đây thay vì báo "chưa có hội thoại".
  const { data: botsData } = useAiBots()
  const role = useAuthStore((s) => s.user?.role)
  const user = useAuthStore((s) => s.user)
  // Chưa có nhân viên AI nào VÀ không hội thoại nào được AI phục vụ → mời tạo (còn hội thoại thì vẫn hiện danh sách).
  const noAiAgent = filter === 'ai' && botsData !== undefined && botsData.bots.length === 0
    && !isLoading && (data?.conversations.length ?? 0) === 0
  const { data: allCounts } = useConversationCounts()
  const firstRun =
    accounts !== undefined &&
    widgets !== undefined &&
    allCounts !== undefined &&
    accounts.length === 0 &&
    widgets.length === 0 &&
    allCounts.total === 0
  const { data: activeConv } = useConversation(activeId)
  const markRead = useMarkRead()
  const markAllRead = useMarkAllRead()

  const conversations = useMemo(() => data?.conversations ?? [], [data])

  // Đánh dấu đã đọc khi mở một hội thoại (một lần cho mỗi id).
  const markedRef = useRef<string | null>(null)
  useEffect(() => {
    if (activeId && markedRef.current !== activeId) {
      markedRef.current = activeId
      markRead.mutate(activeId)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeId])

  function handleSelect(id: string) {
    navigate(`/conversations/${id}${window.location.search}`) // giữ ?sv= (chế độ xem đang chọn)
  }

  async function handleMarkAllRead() {
    try {
      const res = await markAllRead.mutateAsync(accountId)
      toast.success(t('page.markedAllRead', { count: res.updated }))
    } catch {
      toast.error(t('page.markFailed'))
    }
  }

  function handleRefresh() {
    queryClient.invalidateQueries({ queryKey: ['conversations'] })
    queryClient.invalidateQueries({ queryKey: ['conversation-counts'] })
    toast.success(t('page.refreshed'))
  }

  // Bộ lọc điều kiện nâng cao (nút phễu) — dùng ở đầu danh sách (máy tính) và ở tiêu đề ngăn kéo Bộ lọc (điện thoại).
  function advancedFilter(afterApply?: () => void) {
    return (
      <FilterPopover
        conditions={inbox.conditions}
        logic={inbox.logic}
        baseConditions={inbox.baseConditions}
        baseLogic={inbox.baseLogic}
        onApply={(conditions, logic) => { inbox.apply(conditions, logic); afterApply?.() }}
        onSaveAsView={(conditions, logic) => setViewDialog({ open: true, mode: 'create', conditions, logic })}
      />
    )
  }

  // Cột bộ lọc dùng chung: máy tính = cột icon ⇄ mở rộng; điện thoại = ngăn kéo trượt từ trái (variant 'drawer', cùng chức năng).
  function renderFilterRail(drawer?: { onNavigate: () => void; topSlot: React.ReactNode }) {
    return (
      <FilterRail
        filter={filter}
        onFilterChange={handleFilterChange}
        tag={tag}
        onTagChange={setTag}
        accountId={accountId}
        onAccountChange={handleAccountChange}
        channelGroups={channelGroups}
        channel={channelParam ?? 'all'}
        onChannelChange={handleChannelChange}
        userId={meId}
        counts={counts}
        viewActive={!!inbox.activeView}
        variant={drawer ? 'drawer' : 'rail'}
        onNavigate={drawer?.onNavigate}
        topSlot={drawer?.topSlot}
        headerSlot={drawer ? advancedFilter(drawer.onNavigate) : undefined}
        renderCustomViews={(expanded, afterSelect) => (
          <SavedViewsSection
            expanded={expanded}
            activeId={inbox.savedViewId}
            counts={viewCounts}
            onSelect={(v) => {
              setFilter('all')
              setTag(undefined)
              inbox.selectView(v.id)
              afterSelect?.()
            }}
            onDeleted={(id) => id === inbox.savedViewId && inbox.selectView(undefined)}
            onOpenDialog={setViewDialog}
          />
        )}
      />
    )
  }

  const conversationList = (
    <ConversationList
      mobileHeader={isMobile ? (collapsed) => (
        <MobileInboxHeader
          collapsed={collapsed}
          user={{ id: user?.id, impersonated: !!user?.impersonatedBy, fullName: user?.fullName ?? '', avatarUrl: user?.avatarUrl, orgName: user?.org?.name }}
          search={searchInput}
          onSearchChange={setSearchInput}
          accounts={stripAccounts(accounts)}
          accountId={accountId}
          onAccountChange={handleAccountChange}
          filter={filter}
          onFilterChange={handleFilterChange}
          tag={tag}
          onTagChange={setTag}
          viewActive={!!inbox.activeView}
          counts={counts}
          tab={tab}
          onTabChange={setTab}
          tabUnread={{ main: mainCounts?.unread ?? 0, internal: internalCounts?.unread ?? 0 }}
          channelGroups={channelGroups}
          channel={channelParam ?? 'all'}
          onChannelChange={handleChannelChange}
          onNewConversation={() => setNewConvOpen(true)}
          onAddFriend={() => setAddFriendOpen(true)}
          onMarkAllRead={handleMarkAllRead}
          onRefresh={handleRefresh}
          renderFilterRail={(o) => renderFilterRail(o)}
        />
      ) : undefined}
      title={inbox.activeView?.name ?? (tag ? t('rail.tagLabel', { tag }) : FILTER_LABEL[filter])}
      // Bộ lọc điều kiện nâng cao: máy tính = nút phễu ở đầu danh sách; điện thoại = nút phễu trong ngăn kéo Bộ lọc.
      headerActions={isMobile ? undefined : advancedFilter()}
      // Bộ lọc dạng "chế độ xem" (Nhân viên AI, Của tôi…): số hội thoại của CHÍNH bộ lọc đó (khớp danh sách);
      // còn lại: số chưa đọc của tab như trước.
      titleCount={isViewFilter ? (data?.total ?? 0) : (counts?.unread ?? 0)}
      titleCountTone={isViewFilter ? 'total' : 'unread'}
      showAiAgent={filter === 'ai' && !inbox.activeView}
      manyAccountGroups={manyAccountGroups}
      subHeader={filter === 'ai' && !inbox.activeView && (botsData?.bots.length ?? 0) > 0 ? (
        <AiBotChips bots={botsData!.bots.filter((b) => b.enabled)} value={aiBot} onChange={setAiBot} />
      ) : undefined}
      emptyOverride={
        noAiAgent ? (
          <NoAiAgentEmpty canCreate={role === 'owner' || role === 'admin'} onCreate={() => navigate(AI_PATHS.newBot)} />
        ) : undefined
      }
      items={noAiAgent ? [] : conversations}
      activeId={activeId}
      search={searchInput}
      tag={tag}
      isLoading={isLoading}
      hasMore={!!hasNextPage}
      loadingMore={isFetchingNextPage}
      onLoadMore={loadMoreConversations}
      // Lần tải lại lỗi mà vẫn còn dữ liệu cũ → giữ danh sách (tự thử lại ngầm), chỉ hiện màn lỗi khi chưa có gì.
      isError={isError && !data}
      onRetry={() => void refetch()}
      onSearchChange={setSearchInput}
      onTagChange={setTag}
      tab={tab}
      onTabChange={setTab}
      tabUnread={{ main: mainCounts?.unread ?? 0, internal: internalCounts?.unread ?? 0 }}
      channelGroups={channelGroups}
      channel={channelParam ?? 'all'}
      onChannelChange={handleChannelChange}
      onSelect={handleSelect}
      onAddFriend={() => setAddFriendOpen(true)}
      onNewConversation={() => setNewConvOpen(true)}
      onMarkAllRead={handleMarkAllRead}
      onRefresh={handleRefresh}
      scrollMemory={isMobile ? listScrollRef : undefined}
    />
  )

  return (
    <>
      {isMobile ? (
        <div className="h-full min-h-0 overflow-hidden bg-background">
          {activeId ? (
            <ChatPanel key={activeId} convId={activeId} onBack={() => navigate(`/conversations${window.location.search}`)} onOpenInfo={() => setInfoOpen(true)} />
          ) : firstRun ? (
            <ConnectChannelsOnboarding />
          ) : (
            conversationList
          )}
        </div>
      ) : (
      <>
      {/* Hai thẻ nổi trên nền gradient (kiểu SaleSmartly): [lọc + danh sách] và
          [khung chat + thông tin khách]. */}
      <div className="flex h-full min-h-0 gap-2 overflow-hidden">
        <div className="flex shrink-0 overflow-hidden rounded-xl bg-card shadow-[0_0_0_1px_hsl(var(--border))]">
        {/* Cột lọc (icon) */}
        {renderFilterRail()}

        {/* Cột danh sách hội thoại */}
        <aside className="flex shrink-0 flex-col" style={{ width: listWidth }}>
          {conversationList}
        </aside>
        </div>

        {/* Thanh kéo thu gọn cột danh sách (nằm trong khe giữa hai thẻ); nhấn đúp = về mặc định */}
        <div
          onMouseDown={startListResize}
          onDoubleClick={() => setListWidth(LIST_MAX)}
          role="separator"
          aria-orientation="vertical"
          aria-label={t('page.collapseAria')}
          title={t('page.collapseTitle')}
          className="group relative -mx-2 w-2 shrink-0 cursor-col-resize"
        >
          <span
            className={cn(
              'absolute inset-y-3 left-1/2 w-0.5 -translate-x-1/2 rounded-full transition-colors',
              listResizing ? 'bg-primary/60' : 'group-hover:bg-primary/40',
            )}
          />
          <span
            className={cn(
              'absolute left-1/2 top-1/2 z-10 flex h-8 w-4 -translate-x-1/2 -translate-y-1/2 items-center justify-center',
              'rounded-full border bg-card text-muted-foreground shadow-sm transition-opacity',
              listResizing ? 'opacity-100' : 'opacity-0 group-hover:opacity-100',
            )}
          >
            <ChevronsLeftRight className="h-3 w-3" />
          </span>
        </div>

        <div className="flex min-w-0 flex-1 overflow-hidden rounded-xl bg-card shadow-[0_0_0_1px_hsl(var(--border))]">
        {/* Cột giữa: khung chat */}
        <main className="flex min-w-0 flex-1 flex-col">
          {activeId ? (
            <ChatPanel key={activeId} convId={activeId} />
          ) : firstRun ? (
            <ConnectChannelsOnboarding />
          ) : (
            <div className="flex h-full flex-col items-center justify-center gap-2">
              <EmptyState
                icon={MessagesSquare}
                title={t('page.pickTitle')}
                description={t('page.pickDesc')}
              />
              <Link to="/integrations" className="text-sm text-primary hover:underline">
                {t('page.connectMore')}
              </Link>
            </div>
          )}
        </main>

        {/* Cột phải: thông tin khách hàng */}
        {/* Thanh kéo để mở rộng cột thông tin khách hàng */}
        <div
          onMouseDown={startResize}
          role="separator"
          aria-orientation="vertical"
          aria-label={t('page.resizeAria')}
          title={t('page.resizeTitle')}
          className={cn(
            'group relative hidden w-1.5 shrink-0 cursor-col-resize xl:block',
            resizing ? 'bg-primary/40' : 'hover:bg-primary/20',
          )}
        >
          <span
            className={cn(
              'absolute left-1/2 top-1/2 flex h-8 w-4 -translate-x-1/2 -translate-y-1/2 items-center justify-center',
              'rounded-full border bg-card text-muted-foreground shadow-sm transition-opacity',
              resizing ? 'opacity-100' : 'opacity-0 group-hover:opacity-100',
            )}
          >
            <ChevronsLeftRight className="h-3 w-3" />
          </span>
        </div>

        <aside
         
          className="hidden shrink-0 border-l xl:block"
          style={{ width: rightWidth }}
        >
          <AppRail
            apps={activeApps}
            onOpenConnections={() => setConnectionsOpen(true)}
            onOpenAppSettings={() => setAppSettingsOpen(true)}
          >
            {activeId ? (
              <ContactPanel conv={activeConv} />
            ) : (
              <div className="flex h-full items-center justify-center p-6 text-center text-sm text-muted-foreground">
                {t('page.infoPlaceholder')}
              </div>
            )}
          </AppRail>
        </aside>
        </div>
      </div>
      </>
      )}

      {/* Điện thoại: hồ sơ khách dạng hộp trượt */}
      <BottomSheet open={isMobile && infoOpen} onOpenChange={setInfoOpen}>
        <BottomSheetContent title={t('page.customerInfo')} className="h-[85dvh]">
          {activeId ? <ContactPanel conv={activeConv} /> : null}
        </BottomSheetContent>
      </BottomSheet>

      <AddFriendDialog open={addFriendOpen} onOpenChange={setAddFriendOpen} />
      <NewConversationDialog open={newConvOpen} onOpenChange={setNewConvOpen} onOpened={handleSelect} />
      <ViewDialog
        state={viewDialog}
        onClose={() => setViewDialog({ open: false })}
        onSaved={(v) => {
          // Vừa tạo → mở luôn chế độ xem đó (như SaleSmartly).
          if (viewDialog.open && viewDialog.mode === 'create') {
            setFilter('all')
            setTag(undefined)
            inbox.selectView(v.id)
          }
        }}
      />
      <ConnectionsDialog open={connectionsOpen} onOpenChange={setConnectionsOpen} />
      <AppSettingsDialog open={appSettingsOpen} onOpenChange={setAppSettingsOpen} />
    </>
  )
}

/**
 * Bộ lọc "Nhân viên AI" khi tổ chức chưa có nhân viên AI nào (kiểu SaleSmartly "Chưa có AI Agent"):
 * không hiện danh sách, chỉ hình minh hoạ + nút sang Nhân viên AI → Tạo nhân viên AI.
 */
function NoAiAgentEmpty({ canCreate, onCreate }: { canCreate: boolean; onCreate: () => void }) {
  const { t } = useTranslation('convList')
  return (
    <div className="flex flex-col items-center px-6 py-16 text-center">
      <div className="relative mb-4 flex h-28 w-28 items-center justify-center rounded-full bg-primary/[0.06]">
        <div className="flex flex-col items-center gap-1">
          <span className="flex h-9 w-14 items-center justify-center gap-2 rounded-2xl border-2 border-foreground/70 bg-card">
            <span className="h-2.5 w-2.5 rounded-full bg-primary" />
            <span className="h-2.5 w-2.5 rounded-full bg-primary" />
          </span>
          <span className="flex h-8 w-20 items-center justify-end rounded-2xl border-2 border-foreground/70 bg-card pr-3">
            <span className="h-1.5 w-5 rounded-full bg-teal-400" />
          </span>
        </div>
        <Sparkles className="absolute right-1 top-3 h-6 w-6 fill-primary text-primary" />
        <Sparkles className="absolute bottom-7 left-0 h-4 w-4 fill-teal-400 text-teal-400" />
      </div>
      <h3 className="text-sm font-medium">{t('page.noAiTitle')}</h3>
      <p className="mt-1 text-xs leading-relaxed text-muted-foreground">
        {t('page.noAiDesc')}
      </p>
      {canCreate ? (
        <Button variant="secondary" className="mt-4" onClick={onCreate}>
          {t('page.createAi')}
        </Button>
      ) : (
        <p className="mt-4 text-xs text-muted-foreground">{t('page.contactOwner')}</p>
      )}
    </div>
  )
}

/**
 * Bộ lọc "Nhân viên AI" ▸ chọn một nhân viên AI. Nhân viên AI mặc định (`isDefault`, phụ trách kênh chưa giao) ghi
 * "(mặc định)". Server cũ chưa có nhân viên AI mặc định → giữ chip "AI chung" (kênh không gắn nhân viên AI nào).
 */
function AiBotChips({ bots, value, onChange }: {
  bots: Array<{ id: string; name: string; isDefault?: boolean; allChannels: boolean }>
  value: string
  onChange: (v: string) => void
}) {
  const { t } = useTranslation('convList')
  const hasDefaultBot = bots.some((b) => b.isDefault === true)
  const opts = [
    { id: '', name: t('page.allAi') },
    ...bots.map((b) => ({ id: b.id, name: isDefaultBot(b) ? t('page.botDefault', { name: b.name }) : b.name })),
    ...(hasDefaultBot ? [] : [{ id: 'default', name: t('page.generalAi') }]),
  ]
  return (
    <div className="flex shrink-0 items-center gap-1.5 overflow-x-auto px-3 pb-2 pt-1" role="group" aria-label={t('page.filterByAi')}>
      {opts.map((o) => (
        <button
          key={o.id || 'all'}
          type="button"
          onClick={() => onChange(o.id)}
          title={o.id === 'default' ? t('page.generalAiHint') : undefined}
          className={cn(
            'shrink-0 whitespace-nowrap rounded-full border px-2.5 py-0.5 text-xs font-medium transition-colors',
            value === o.id ? 'border-primary bg-primary text-primary-foreground' : 'border-border bg-card text-muted-foreground hover:bg-muted',
          )}
        >
          {o.name}
        </button>
      ))}
    </div>
  )
}
